/**
 * .tuMetrics {"tu1": 0.85, "tu2": 0.87, "tu3": 5, "notes": "Company store and settings", "tuVersion": "2.5"}
 * .help "Company state and settings (e.g. resourcesEnabled). Used for nav and module gating."
 */
// src/store/companyStore.ts
import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { Company, type DashboardPanelConfig } from '../types'; // Import Company type from src/types/index.ts

// July 2026: types/supabase.ts (generated Database types) is an empty file, so
// the old `Database['public']['Tables']['companies']` lookups never type-checked.
// Hand-written row shapes until codegen is wired up. Newer columns (domain,
// resources_enabled, dashboard_panels, teambots_*) are still read via `(row as any)`.
interface CompanyRow {
  id: string;
  name: string;
  logo_url: string | null;
  created_at: string;
  use_company_logo: boolean | null;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  subscription_status: string | null;
  trial_ends_at: string | null;
  plan_id: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean | null;
  tozny_realm_name: string | null;
  tozny_app_name: string | null;
  headquarters_timezone: string | null;
  headquarters_label: string | null;
  day_hours?: number | null;
  hours_edit_through?: string | null;
}

type CompanyUpdate = Partial<Omit<CompanyRow, 'id' | 'created_at'>> & {
  domain?: string | null;
  resources_enabled?: boolean;
  dashboard_panels?: DashboardPanelConfig | null;
};

// Helper: map database row -> frontend type
const mapCompanyFromDB = (row: CompanyRow): Company => ({
  id: row.id,
  name: row.name,
  logoUrl: row.logo_url || undefined,
  createdAt: row.created_at,
  useCompanyLogo: row.use_company_logo || false,
  // August 19, 2025 3:08 PM: Added Stripe-related fields
  stripeCustomerId: row.stripe_customer_id || undefined,
  stripeSubscriptionId: row.stripe_subscription_id || undefined,
  subscriptionStatus: row.subscription_status || undefined,
  trialEndsAt: row.trial_ends_at || undefined,
  planId: row.plan_id || undefined,
  currentPeriodStart: row.current_period_start || undefined,
  currentPeriodEnd: row.current_period_end || undefined,
  cancelAtPeriodEnd: row.cancel_at_period_end || false,
  // September 24, 2025 6:37 PM: Added Tozny-related fields
  toznyRealmName: row.tozny_realm_name || undefined,
  toznyAppName: row.tozny_app_name || undefined,
  // November 22, 2025: Added domain for guest detection
  domain: (row as any).domain || undefined,
  // Resources module
  resourcesEnabled: (row as any).resources_enabled ?? false,
  // Dashboard panel config (order + visibility)
  dashboardPanels: (row as any).dashboard_panels as DashboardPanelConfig | undefined,
  teambotsOrgId: (row as any).teambots_org_id ?? null,
  teambotsOrgConnectionStatus: (row as any).teambots_org_connection_status ?? null,
  teambotsConnectedAt: (row as any).teambots_connected_at ?? null,
  headquartersTimezone: row.headquarters_timezone ?? null,
  headquartersLabel: row.headquarters_label ?? null,
  dayHours: row.day_hours ?? 8,
  hoursEditThrough: row.hours_edit_through ?? null,
});

// Helper: map frontend partial updates -> DB update
const mapCompanyToDBUpdate = (updates: Partial<Company>): CompanyUpdate => {
  const dbUpdates: CompanyUpdate = {};
  if (updates.name !== undefined) dbUpdates.name = updates.name;
  if (updates.logoUrl !== undefined) dbUpdates.logo_url = updates.logoUrl ?? null;
  if (updates.useCompanyLogo !== undefined) dbUpdates.use_company_logo = updates.useCompanyLogo;
  // August 19, 2025 3:08 PM: Added Stripe-related fields for update
  if (updates.stripeCustomerId !== undefined) dbUpdates.stripe_customer_id = updates.stripeCustomerId ?? null;
  if (updates.stripeSubscriptionId !== undefined) dbUpdates.stripe_subscription_id = updates.stripeSubscriptionId ?? null;
  if (updates.subscriptionStatus !== undefined) dbUpdates.subscription_status = updates.subscriptionStatus ?? null;
  if (updates.trialEndsAt !== undefined) dbUpdates.trial_ends_at = updates.trialEndsAt ?? null;
  if (updates.planId !== undefined) dbUpdates.plan_id = updates.planId ?? null;
  if (updates.currentPeriodStart !== undefined) dbUpdates.current_period_start = updates.currentPeriodStart ?? null;
  if (updates.currentPeriodEnd !== undefined) dbUpdates.current_period_end = updates.currentPeriodEnd ?? null;
  if (updates.cancelAtPeriodEnd !== undefined) dbUpdates.cancel_at_period_end = updates.cancelAtPeriodEnd;
  // September 24, 2025 6:37 PM: Added Tozny-related fields for update
  if (updates.toznyRealmName !== undefined) dbUpdates.tozny_realm_name = updates.toznyRealmName ?? null;
  if (updates.toznyAppName !== undefined) dbUpdates.tozny_app_name = updates.toznyAppName ?? null;
  // November 22, 2025: Added domain for guest detection
  if (updates.domain !== undefined) dbUpdates.domain = updates.domain ?? null;
  // Resources module (admin toggle)
  if (updates.resourcesEnabled !== undefined) dbUpdates.resources_enabled = updates.resourcesEnabled;
  // Dashboard panel config
  if (updates.dashboardPanels !== undefined) dbUpdates.dashboard_panels = updates.dashboardPanels ?? null;
  if (updates.headquartersTimezone !== undefined) {
    dbUpdates.headquarters_timezone = updates.headquartersTimezone ?? null;
  }
  if (updates.headquartersLabel !== undefined) {
    dbUpdates.headquarters_label = updates.headquartersLabel ?? null;
  }
  if (updates.dayHours !== undefined) {
    dbUpdates.day_hours = updates.dayHours;
  }
  if (updates.hoursEditThrough !== undefined) {
    dbUpdates.hours_edit_through = updates.hoursEditThrough ?? null;
  }
  return dbUpdates;
};

interface CompanyState {
  company: Company | null;
  isLoading: boolean;
  error: string | null;
  initializeCompanySettings: (companyId: string | undefined) => Promise<void>;
  updateCompanySettings: (updates: Partial<Company>) => Promise<void>;
  deleteCompany: (companyId: string) => Promise<void>;
  fetchFirstCompanyId: () => Promise<string | null>; // September 25, 2025 8:21 PM UTC: New action
  fetchCompanyByRealmName: (realmName: string) => Promise<Company | null>; // September 25, 2025 8:40 PM UTC: New action
}

const useCompanyStore = create<CompanyState>((set, _get) => ({
  company: null,
  isLoading: false,
  error: null,

  /**
   * Load company from `public.companies`.
   * NOTE: This replaces any legacy references to a `company_settings` table.
   */
  initializeCompanySettings: async (companyId) => {
    set({ isLoading: true, error: null });

    try {
      if (!companyId) {
        console.warn(
          'initializeCompanySettings: No companyId provided. Skipping company initialization.'
        );
        set({ company: null, isLoading: false });
        return;
      }

      console.log(
        `initializeCompanySettings: Fetching company from 'companies' for company ID: ${companyId}`
      );

      const { data, error } = await supabase
        .from('companies')
        .select(`
          id,
          name,
          logo_url,
          use_company_logo,
          created_at,
          stripe_customer_id,
          stripe_subscription_id,
          subscription_status,
          trial_ends_at,
          plan_id,
          current_period_start,
          current_period_end,
          cancel_at_period_end,
          tozny_realm_name,
          tozny_app_name,
          domain,
          resources_enabled,
          dashboard_panels,
          headquarters_timezone,
          headquarters_label,
          day_hours,
          hours_edit_through
        `)
        .eq('id', companyId)
        .single<CompanyRow>();

      if (error) {
        // PGRST116: No rows
        if ((error as any).code === 'PGRST116') {
          const msg = `Company not found for ID ${companyId}`;
          console.error('initializeCompanySettings:', msg);
          throw new Error(msg);
        }
        console.error(
          `initializeCompanySettings: Error fetching company for ID ${companyId}:`,
          error
        );
        throw error;
      }

      set({ company: mapCompanyFromDB(data), isLoading: false });
      console.log('companyStore: Initialized company ID:', data.id);
    } catch (err: any) {
      console.error('Error initializing company settings:', err);
      set({
        error: `Failed to load company: ${err.message ?? 'Unknown error'}`,
        isLoading: false,
      });
    }
  },

  /**
   * Update company in `public.companies` for the current user's companyId.
   */
  updateCompanySettings: async (updates) => {
    set({ isLoading: true, error: null });

    try {
      // Import useUserStore lazily to avoid circular deps at module load time
      const useUserStore = (await import('./userStore')).default;
      const currentUser = useUserStore.getState().currentUser;

      if (!currentUser?.companyId) {
        throw new Error('No current user companyId found. Cannot update company.');
      }

      const dbUpdates = mapCompanyToDBUpdate(updates);

      const { data, error } = await supabase
        .from('companies')
        .update(dbUpdates)
        .eq('id', currentUser.companyId)
        .select(`
          id,
          name,
          logo_url,
          use_company_logo,
          created_at,
          stripe_customer_id,
          stripe_subscription_id,
          subscription_status,
          trial_ends_at,
          plan_id,
          current_period_start,
          current_period_end,
          cancel_at_period_end,
          tozny_realm_name,
          tozny_app_name,
          domain,
          resources_enabled,
          dashboard_panels,
          headquarters_timezone,
          headquarters_label,
          day_hours,
          hours_edit_through
        `)
        .single<CompanyRow>();

      if (error) throw error;

      set({ company: mapCompanyFromDB(data), isLoading: false });
      console.log('companyStore: Updated company ID:', data.id);
    } catch (err: any) {
      console.error('Error updating company settings:', err);
      set({
        error: `Failed to update company settings: ${err.message ?? 'Unknown error'}`,
        isLoading: false,
      });
    }
  },

  /**
   * Delete company via edge function and sign the user out.
   */
  deleteCompany: async (companyId: string) => {
    set({ isLoading: true, error: null });

    try {
      const { error: deleteError } = await supabase.functions.invoke('delete-company', {
        body: { company_id: companyId },
      });

      if (deleteError) {
        console.error('Error invoking delete-company function:', deleteError);
        throw deleteError;
      }

      // Import useUserStore lazily to avoid circular deps at module load time
      const useUserStore = (await import('./userStore')).default;
      set({ company: null, isLoading: false });
      useUserStore.getState().signOut();
    } catch (err: any) {
      console.error('Error deleting company:', err);
      set({
        error: `Failed to delete company: ${err.message ?? 'Unknown error'}`,
        isLoading: false,
      });
    }
  },

  // September 25, 2025 8:21 PM UTC: New action to fetch the ID of the first company
  fetchFirstCompanyId: async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id')
        .limit(1);

      if (error) {
        console.error('fetchFirstCompanyId: Error fetching first company ID:', error);
        return null;
      }
      return data?.[0]?.id || null;
    } catch (error) {
      console.error('fetchFirstCompanyId: Unexpected error:', error);
      return null;
    }
  },

  // September 25, 2025 8:40 PM UTC: New action to fetch company by realm name
  fetchCompanyByRealmName: async (realmName: string) => {
    set({ isLoading: true, error: null });
    try {
      // CRITICAL FIX: Call the new Edge Function to bypass RLS
      const { data, error } = await supabase.functions.invoke('fetch-company-by-realm-name', {
        body: { realmName },
      });

      if (error) {
        console.error(`fetchCompanyByRealmName: Error invoking Edge Function for realm name ${realmName}:`, error);
        throw error;
      }

      const fetchedCompany = data as CompanyRow | null; // The Edge Function returns a single object or null

      if (!fetchedCompany) {
        console.log(`companyStore: No company found for realm name: ${realmName}`);
        set({ company: null, isLoading: false });
        return null;
      }

      set({ company: mapCompanyFromDB(fetchedCompany), isLoading: false });
      console.log(`companyStore: Initialized company ID ${fetchedCompany.id} by realm name ${realmName}`);
      return mapCompanyFromDB(fetchedCompany);
    } catch (err: any) {
      console.error('Error fetching company by realm name:', err);
      set({
        error: `Failed to load company by realm name: ${err.message ?? 'Unknown error'}`,
        isLoading: false,
      });
      return null;
    }
  },
}));

export default useCompanyStore;
